import React, { useEffect, useState } from "react";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PayPalCheckout
 * ---------------
 * Renders PayPal Smart Buttons that create + capture an order via our backend.
 *
 * Props:
 *   amountUsd        (number)   required — dollars.
 *   purpose          (string)   "booking_deposit" | "booking_full" | "hold_fee"
 *   bookingId        (string)   optional
 *   description      (string)   optional
 *   parentToken      (string)   optional — parent JWT to attribute the payment
 *   onSuccess({id, amount_usd, booking_id})  — captured
 *   onError(err)     optional
 */
const PayPalCheckout = ({
  amountUsd,
  purpose = "booking_deposit",
  bookingId = "",
  description = "",
  parentToken = "",
  onSuccess,
  onError,
}) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/public/config`).then(({ data }) => {
      if (alive) { setConfig(data); setLoading(false); }
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading secure checkout…</div>;
  }
  if (!config?.paypal_ready || !config.paypal_client_id) {
    return <div className="text-xs text-destructive">Payments are not currently configured. Please try again later.</div>;
  }

  const createOrder = async () => {
    try {
      const { data } = await axios.post(
        `${API}/payments/paypal/create-order`,
        { amount_usd: amountUsd, purpose, booking_id: bookingId, description },
        parentToken ? { headers: { Authorization: `Bearer ${parentToken}` } } : undefined,
      );
      return data.id;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start PayPal checkout");
      throw e;
    }
  };

  const onApprove = async (data) => {
    try {
      const { data: cap } = await axios.post(`${API}/payments/paypal/capture/${data.orderID}`);
      toast.success(`Payment received — $${cap.amount_usd?.toFixed?.(2) || amountUsd.toFixed(2)}`);
      onSuccess?.(cap);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment could not be captured");
      onError?.(e);
    }
  };

  return (
    <div data-testid="paypal-checkout" className="space-y-3">
      <PayPalScriptProvider options={{ clientId: config.paypal_client_id, currency: "USD", intent: "capture" }}>
        <PayPalButtons
          style={{ layout: "vertical", color: "black", shape: "pill", label: "pay" }}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={(err) => { toast.error("PayPal error — please try again."); onError?.(err); }}
        />
      </PayPalScriptProvider>
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <ShieldCheck size={11} className="text-primary" />
        Secured by PayPal · {config.paypal_env === "live" ? "Live" : "Sandbox"} mode
      </div>
    </div>
  );
};

export default PayPalCheckout;
